import { DayHour, DayNumber, MapLike, MinuteNumber, MonthNumber, ValidBusinessObject } from '@iotv/iotv-v3-types'
import { Milliseconds } from '../../../../constants'
import { DailyDecadeBasedArrayDef, DailyDecadeBasedArrayDefType } from '../sharedTypes'
import { DimensionedPeriodizer, DimensionedPeriodizerProps } from './DimensionedPeriodizer'
import { DEBUG_OVERRIDE, NOT_LAMBDA, TimeBucketType, TimeBucketUnits, daysInLastUTCDecade, monthDiffYearsDaysDiff, daysInThisUTCMonth } from '../../../../util'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

const className = 'DailyDecadePeriodizer'

export type DailyDecadePeriodizerProps = DimensionedPeriodizerProps & {
  arrayLength?: DailyDecadeBasedArrayDefType
  timeBucketDefiniion?: TimeBucketType & {
    unit: typeof TimeBucketUnits.DAY
    width: 1
  }
}

/**
 * @class QuarterHourPeriodizer manages periodization for 1 month of quarter hour peridos
 */
export class DailyDecadePeriodizer extends DimensionedPeriodizer<MapLike<number | null>, DailyDecadeBasedArrayDefType> {
  constructor(ob?: DailyDecadePeriodizerProps & ValidBusinessObject) {
    super(ob)
    if (!ob) {
      this.type = className
      this.arrayLengths = DailyDecadeBasedArrayDef
      this.initialize()
    }
  }

  timeBucketDefinition: TimeBucketType = {
    unit: TimeBucketUnits.DAY,
    width: 1,
  }

  timeBucketToAccessor = (timeBucketAccessor: [number, MonthNumber, DayNumber, DayHour, MinuteNumber, MinuteNumber]) => {
    const [y, m, d, h, min, s] = timeBucketAccessor
    const thisAccessor: [number, MonthNumber, DayNumber] = [y % 10, m, (d - 1) as DayNumber]
    return thisAccessor
  }

  periodizerDaysLength = () => daysInLastUTCDecade(this.lastUpdatedTimestamp ?? 0)

  getFirstPossibleTimestamp = () => (this.lastUpdatedTimestamp ?? 0) - this.periodizerDaysLength() * Milliseconds.DAY + 1 * Milliseconds.DAY

  accessorToTimestamp = (accessor: number[]): number => {
    const decadeOffset = Math.floor(new Date(this.lastUpdatedTimestamp ?? 0).getUTCFullYear() / 10) * 10
    const baseDate = new Date()
    baseDate.setUTCFullYear(decadeOffset + accessor[0], accessor[1], accessor[2] + 1)
    const impliedTimestamp = baseDate.valueOf()
    return impliedTimestamp
  }

  /**
   * @deprecated
   * @function accessorToTimestampParked for unknown reason gives bad future dates which don't correctly deal with leap years
   * @param accessor
   * @returns
   */
  accessorToTimestampParked = (accessor: number[]): number => {
    const startTimestamp = this.lastUpdatedTimestamp ?? 0
    const currentAccessor = this.timestampToAccessor(startTimestamp)
    const [_yearDiff, _monthsDiff, daysDiff] = accessor.map((v, i) => v - currentAccessor[i])

    // handle rollover by when days same hour same quarter more, days same hour more, days more
    let absoluteNegativeDiff: number | undefined
    const [currentDYear, currentMonth] = currentAccessor // probably need just on on this or lastUpdatedTimestamp for year
    const [accessorDYear, accessorMonth] = accessor

    const decadeOffset = Math.floor(new Date(this.lastUpdatedTimestamp ?? 0).getUTCFullYear() / 10) * 10
    console.log('decardOffset', decadeOffset)
    const accessorYear = decadeOffset + accessorDYear
    const currentYear = decadeOffset + currentDYear

    const monthDaysDiffRes = monthDiffYearsDaysDiff(accessorYear, accessorMonth, currentYear, currentMonth)
    const relativeDiffDays = monthDaysDiffRes + daysDiff
    const relativeDiff = relativeDiffDays * Milliseconds.DAY

    debug && console.log({ monthDaysDiffRes, daysDiff, relativeDiffDays })

    if (relativeDiff > 0) {
      // absolute diff A + B is length of bucket minus how far ahead the accessor is xxxxx  [<---A---xxxxx--B-->]
      absoluteNegativeDiff = -(this.periodizerDaysLength() * Milliseconds.DAY - relativeDiff)
    } else {
      absoluteNegativeDiff = relativeDiff
    }

    const impliedTimestamp = startTimestamp + absoluteNegativeDiff

    // if ( accessor.every( ( v, i) => v === 0 ) ) { console.log({ accessor, currentAccessor, _monthsDiff, daysDiff, hoursDiff, monthDaysDiffRes, relativeDiff, impliedTimestamp, date: new Date(impliedTimestamp).toUTCString() }) }

    return impliedTimestamp
  }

  isValidAccessor = (args: number[]): args is DailyDecadeBasedArrayDefType => {
    const date = new Date(this.lastUpdatedTimestamp ?? 0)
    const currentYear = date.getUTCFullYear()
    const diff = args[0] - (currentYear % 10)
    date.setUTCFullYear(currentYear + diff, args[1], 1)

    const maxDays = daysInThisUTCMonth(date.valueOf())
    //debug && console.log(`In current year ${ currentYear } with diff ${ diff } max days for ${ args.join()}, ${maxDays} using ${date.toUTCString()}`)

    const isValid = args.length === this.arrayLengths?.length && args.every((d, i) => d >= 0) && args[1] <= 11 && args[2] <= maxDays - 1
    if (!isValid) {
      debug && console.log(`Not valid accessor`, args)
    }
    debug && console.log({ accessor: args, maxDays, isValid, date: date.toUTCString() })
    return isValid
  }
}
